import { inject, Injectable } from "@angular/core";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Checklist } from "../../models/checklist.model";
import { ChecklistApiService } from "./checklist-api.service";
import {
  ChecklistState,
  ChecklistStoreService,
} from "./checklist-store.service";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { Poste } from "app/features/production/models/poste.model";

@Injectable({ providedIn: "root" })
export class ChecklistFacadeService extends EntityFacadeService<
  Checklist,
  ChecklistApiService,
  ChecklistState,
  EntityMetadata,
  ChecklistStoreService
> {
  constructor() {
    const api = inject(ChecklistApiService);
    const store = inject(ChecklistStoreService);
    super(api, store);
    store.initStatus(ChecklistFacadeService);
  }

  // ⚠ Sélecteurs tolérants à une entrée VIDE : `getById` sur un id inconnu pose une
  // entrée `null` dans le store.
  getByListId(listId: string) {
    return this.elements()
      .filter((e) => e()?.listId === listId && !e().parent)
      .map((e) => e())
      .sort((a, b) => a.index - b.index);
  }

  getByBundleId(bundleId: string) {
    return this.elements()
      .filter((e) => e()?.bundleId === bundleId && !!e().parent)
      .map((e) => e())
      .sort((a, b) => a.index - b.index);
  }

  getByOfId(ofId: string) {
    return this.elements()
      .filter((e) => !!ofId && e()?.ofId === ofId)
      .map((e) => e())
      .sort((a, b) => a.index - b.index);
  }

  @Untrack
  @UpdateStatus
  async loadByListId(listId: string): Promise<void> {
    this.store.incrementLoading();
    try {
      this.store.addMany(await firstValueFrom(this.api.loadByListId(listId)));
      this.store.decrementLoading();
    } catch (e) {
      this.store.decrementLoading();
      this.store.patchState({
        error: e,
      });
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async loadChecklistForOf(ofId: string): Promise<void> {
    this.store.incrementLoading();
    try {
      this.store.addMany(
        await firstValueFrom(this.api.loadChecklistsForOf(ofId)),
      );
      this.store.decrementLoading();
    } catch (e) {
      this.store.decrementLoading();
      this.store.patchState({
        error: e,
      });
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async duplicate(cl: Checklist): Promise<void> {
    await this.update(cl, this.api.duplicate);
  }

  @Untrack
  @UpdateStatus
  async saveAll(checklists: Checklist[]): Promise<void> {
    this.store.addMany(await firstValueFrom(this.api.saveAll(checklists)));
  }

  /** Jeton de la DERNIÈRE demande de bundle : une réponse plus ancienne est ignorée (CL-25). */
  private bundleToken = 0;
  /** Dernière période demandée — ce que `reloadBundle()` relit (après un conflit, au retour sur l'onglet…). */
  private lastBundleRequest: { listId: string; date: string; dateFin?: string } | null = null;

  /**
   * Bundle d'une période : pose `currentBundle` et REMPLACE les instances de ce bundle dans le
   * store (CL-25) — une instance supprimée entre-temps (réalisation annulée par un collègue,
   * instance pré-créée remplacée) disparaît au lieu de survivre dans le store.
   *
   * ⚠ Deux demandes rapprochées (« jour précédent » deux fois, rechargement périodique pendant
   * un changement de jour) peuvent répondre dans le désordre : seule la DERNIÈRE demandée est
   * appliquée, les autres sont ignorées (sinon la frise restait vide, sans message).
   */
  @Untrack
  @UpdateStatus
  async loadBundleFor(listId: string, date: string, dateFin?: string) {
    const token = ++this.bundleToken;
    this.lastBundleRequest = { listId, date, dateFin };
    const bundle = await firstValueFrom(
      this.api.loadBundleFor(listId, date, dateFin),
    );
    if (token !== this.bundleToken || !bundle) {
      return;
    }
    const recues = new Set((bundle.checklists ?? []).map((c) => c.id));
    for (const e of this.elements()) {
      const cl = e();
      if (cl?.bundleId === bundle.id && !!cl.parent && !recues.has(cl.id)) {
        this.store.removeOne(cl.id);
      }
    }
    for (const checklist of bundle.checklists ?? []) {
      this.store.upsertOne(checklist);
    }

    this.store.patchState({ currentBundle: bundle });
  }

  /**
   * Relit la dernière période demandée (sans rien faire s'il n'y en a pas). Appelé par le dialog
   * d'exécution après un conflit (409 : créneau pris, version périmée, OF déjà construit) et par
   * la page (retour sur l'onglet, rafraîchissement périodique). Erreur avalée : l'intercepteur a
   * déjà affiché le message, et un rechargement d'arrière-plan ne doit rien casser.
   */
  async reloadBundle(): Promise<void> {
    const req = this.lastBundleRequest;
    if (!req) return;
    await this.loadBundleFor(req.listId, req.date, req.dateFin).catch(() => undefined);
  }

  @Untrack
  @UpdateStatus
  async deleteDoneChecklist(checklistId: string): Promise<void> {
    const bundle = await firstValueFrom(this.api.deleteDone(checklistId));
    this.store.removeOne(checklistId);
    this.store.patchState({ currentBundle: bundle });
  }

  @Untrack
  @UpdateStatus
  async buildChecklistForOpe(
    checklist: Checklist,
    ope: Poste,
  ): Promise<Checklist> {
    const saved = await firstValueFrom(
      this.api.buildChecklistForOpe(checklist, ope),
    );
    this.store.upsertOne(saved);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async buildChecklist(checklist: Checklist): Promise<Checklist> {
    const saved = await firstValueFrom(this.api.buildChecklist(checklist));
    this.store.upsertOne(saved);
    return saved;
  }
}
